/**
 * The list's filters (PRD §11: age band, AUM band, last contact date, signal) and name search. The
 * priority filter is the row of cards above the list, so it is not repeated here.
 * "Last contact" is shown as last meeting, because only meetings are read, not calls or emails.
 *
 * The search runs on Enter or on leaving the box. One character is not searched: the server refuses
 * it, and a refused search used to replace the whole page with an error. The box says so instead.
 */
import { useState } from 'react';
import { Search } from 'lucide-react';
import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@design-system';
import type { Prospecting } from '@client/api/types';
import { MIN_SEARCH_LENGTH } from '../hooks/use-prospect-filters';
import { AGE_BAND_LABELS, ASSETS_BAND_LABELS, LAST_MEETING_LABELS, SIGNAL_LABELS } from '../lib/labels';

const ANY = '__any';

interface FilterSelectProps {
  label: string;
  value: string | undefined;
  options: Record<string, string>;
  onChange: (value: string | undefined) => void;
}

function FilterSelect({ label, value, options, onChange }: FilterSelectProps) {
  return (
    <Select value={value ?? ANY} onValueChange={next => onChange(next === ANY ? undefined : next)}>
      <SelectTrigger className="w-full md:w-40 2xl:w-44" aria-label={label}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY}>{`Any ${label.toLowerCase()}`}</SelectItem>
        {Object.entries(options).map(([key, text]) => (
          <SelectItem key={key} value={key}>
            {text}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface ProspectsFiltersProps {
  query: Prospecting.ProspectListQuery;
  hasFilters: boolean;
  onChange: (key: 'ageBand' | 'assetsBand' | 'lastMeeting' | 'signal' | 'q', value: string | undefined) => void;
  onClear: () => void;
}

export function ProspectsFilters({ query, hasFilters, onChange, onClear }: ProspectsFiltersProps) {
  const [tooShort, setTooShort] = useState(false);

  const search = (text: string) => {
    const term = text.trim();
    const short = term.length > 0 && term.length < MIN_SEARCH_LENGTH;
    setTooShort(short);
    if (!short) onChange('q', term || undefined);
  };

  return (
    <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-start">
      <div className="w-full md:w-52 2xl:w-60">
        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            // Remounts when the search is cleared elsewhere, so the box never shows a search that is gone.
            key={query.q ?? ''}
            className="pl-9"
            placeholder="Search by name or email"
            defaultValue={query.q ?? ''}
            onKeyDown={event => {
              if (event.key === 'Enter') search(event.currentTarget.value);
            }}
            onBlur={event => search(event.currentTarget.value)}
            aria-label="Search prospects"
            aria-invalid={tooShort}
          />
        </div>
        {tooShort && (
          <p className="mt-1 text-xs text-gray-600">Type at least {MIN_SEARCH_LENGTH} characters to search.</p>
        )}
      </div>
      <FilterSelect label="Age" value={query.ageBand} options={AGE_BAND_LABELS} onChange={value => onChange('ageBand', value)} />
      <FilterSelect label="Assets" value={query.assetsBand} options={ASSETS_BAND_LABELS} onChange={value => onChange('assetsBand', value)} />
      <FilterSelect label="Last meeting" value={query.lastMeeting} options={LAST_MEETING_LABELS} onChange={value => onChange('lastMeeting', value)} />
      <FilterSelect label="Signal" value={query.signal} options={SIGNAL_LABELS} onChange={value => onChange('signal', value)} />
      {hasFilters && (
        <Button variant="ghost" size="sm" onClick={onClear}>
          Clear filters
        </Button>
      )}
    </div>
  );
}
